import { AppImage } from "@/shared/ui/AppImage";
import { Checkbox, Input, useDebounceCallback } from "@devhop/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";

export interface InfiniteCheckboxListProps<T> {
  queryKey: Array<string>;
  queryFn: (params: {
    search: string;
    pageParam: number;
  }) => Promise<{ items: Array<T>; nextPage: number | null }>;
  preloadQueryFn?: (ids: string[]) => Promise<Array<T>>;
  getLabel: (item: T) => string;
  getValue: (item: T) => string;
  /** Optional: แสดง thumbnail ด้านซ้ายของแต่ละแถว (key, path หรือ URL) */
  getImageSrc?: (item: T) => string | null | undefined;
  values: string[];
  onValuesChange: (values: string[]) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  listHeight?: number;
  className?: string;
}

const DEFAULT_LIST_HEIGHT = 300;

export function InfiniteCheckboxList<T>({
  queryKey,
  queryFn,
  preloadQueryFn,
  getLabel,
  getValue,
  getImageSrc,
  values,
  onValuesChange,
  searchPlaceholder = "ຄົ້ນຫາຕາມຊື່...",
  emptyMessage = "ບໍ່ພົບລາຍການ",
  listHeight = DEFAULT_LIST_HEIGHT,
  className,
}: InfiniteCheckboxListProps<T>) {
  const rowHeight = getImageSrc ? 56 : 44;
  const [search, setSearch] = React.useState("");
  const parentRef = React.useRef<HTMLDivElement>(null);

  const debouncedSetSearch = useDebounceCallback(
    (...args: Array<unknown>) => setSearch(args[0] as string),
    400,
  );

  const { data: preloadedItems } = useQuery({
    queryKey: [...queryKey, "preload", values],
    queryFn: () =>
      preloadQueryFn ? preloadQueryFn(values) : Promise.resolve([]),
    enabled: values.length > 0 && !!preloadQueryFn,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [...queryKey, search],
      queryFn: ({ pageParam = 1 }) => queryFn({ search, pageParam }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      refetchOnWindowFocus: false,
    });

  const items = data ? data.pages.flatMap((page) => page.items) : [];

  const mergedItems = React.useMemo(() => {
    if (!preloadQueryFn || !preloadedItems?.length) return items;
    const existingIds = new Set(items.map((item) => getValue(item)));
    const fromPreload = preloadedItems.filter(
      (item) => !existingIds.has(getValue(item)),
    );
    return [...fromPreload, ...items];
  }, [items, preloadedItems, preloadQueryFn, getValue]);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? mergedItems.length + 1 : mergedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
  });

  React.useEffect(() => {
    const virtualItems = rowVirtualizer.getVirtualItems();
    const lastItem = virtualItems[virtualItems.length - 1];
    if (
      lastItem &&
      lastItem.index >= mergedItems.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    mergedItems.length,
    isFetchingNextPage,
    rowVirtualizer,
  ]);

  const toggle = React.useCallback(
    (id: string) => {
      if (values.includes(id)) {
        onValuesChange(values.filter((v) => v !== id));
      } else {
        onValuesChange([...values, id]);
      }
    },
    [values, onValuesChange],
  );

  return (
    <div className={className}>
      <Input
        type="search"
        placeholder={searchPlaceholder}
        className="mb-2 h-9"
        onChange={(e) => debouncedSetSearch(e.target.value)}
        aria-label={searchPlaceholder}
      />
      <div
        ref={parentRef}
        className="overflow-auto rounded-md border bg-background"
        style={{ height: listHeight }}
      >
        {isFetching && mergedItems.length === 0 && (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            ກຳລັງໂຫຼດ...
          </div>
        )}
        {!isFetching && mergedItems.length === 0 && (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            {emptyMessage}
          </div>
        )}
        {mergedItems.length > 0 && (
          <div
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > mergedItems.length - 1;
              const item = mergedItems[virtualRow.index];

              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  className="flex items-center gap-2 px-3"
                >
                  {isLoaderRow ? (
                    <div className="py-2 text-muted-foreground text-sm">
                      {hasNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ບໍ່ມີລາຍການເພີ່ມເຕີມ"}
                    </div>
                  ) : item ? (
                    <label
                      htmlFor={getValue(item)}
                      className="flex h-full w-full cursor-pointer items-center gap-3 py-1.5 hover:bg-muted/50"
                    >
                      <Checkbox
                        id={getValue(item)}
                        checked={values.includes(getValue(item))}
                        onCheckedChange={() => toggle(getValue(item))}
                        aria-label={getLabel(item)}
                      />
                      {getImageSrc?.(item) ? (
                        <div className="size-10 shrink-0 overflow-hidden rounded-md border bg-muted">
                          <AppImage
                            src={getImageSrc(item)}
                            alt=""
                            className="size-full"
                            fit="cover"
                            showLoading={false}
                          />
                        </div>
                      ) : null}
                      <span
                        className="min-w-0 flex-1 truncate text-sm"
                        title={getLabel(item)}
                      >
                        {getLabel(item)}
                      </span>
                    </label>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
